<template>
  <div class="article-container">
    <Hero
      v-if="articleData"
      ref="hero"
      :heroData="articleData.hero"
      :title="articleData.title"
      :author="articleData.author"
    />

    <div v-if="articleData" class="article-content">
      <div class="article-body">
        <div class="article-module" v-for="(mod, index) in articleData.modules" :key="`module-${index}`">
          <!-- Standard Modules -->
          <ImageBlock v-if="mod.type === 'image'" :moduleData="mod" />
          <ImageFullWidth v-else-if="mod.type === 'full-width-image'" :moduleData="mod" />
          <ImageSequence v-else-if="mod.type === 'image-sequence'" :moduleData="mod" />
          <VideoBlock v-else-if="mod.type === 'video'" :moduleData="mod" />
          <RichText v-else-if="mod.type === 'rich-text'" :moduleData="mod" />

          <!-- Data visualization -->
          <BarChart v-else-if="mod.type === 'bar-chart'" :dataUrl="mod.chartDataUrl" :chartId="`${index}`" />
          <PieChart v-else-if="mod.type === 'pie-chart'" :dataUrl="mod.chartDataUrl" :chartId="`${index}`" />
          <BubbleChart v-else-if="mod.type === 'bubble-chart'" :dataUrl="mod.chartDataUrl" :chartId="`${index}`" />

          <!-- Bespoke Elements -->
          <BettingBarChart v-else-if="mod.type === 'betting-bar-chart'" />
        </div>
      </div>

      <div v-if="suggestionData.length > 0" class="suggestions container">
        <p class="header">You may also like</p>
        <div class="suggested">
          <div
            v-for="(suggestion, index) in suggestionData"
            :key="`suggestion-${index}`"
            class="suggestion-item"
          >
            <a :href="`/${suggestion.type === 'article' ? 'articles' : 'stories' }/${suggestion.id}`">
              <div class="suggestion-img" :style="`background-image: url(${suggestion.thumbnail || ''})`" />
              <h3>{{ suggestion.title }}</h3>
            </a>
          </div>
        </div>
      </div>

      <div class="back-container container">
        <a href="/stories">
          <button>Back to Hub</button>
        </a>
      </div>
    </div>
    <div v-if="loading" class="global-loader">
      <v-progress-circular size="80" indeterminate color="white" />
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
const fb = require('@/firebase')

import Hero from '@/components/articles/Hero'
import ImageBlock from '@/components/articles/ImageBlock'
import ImageFullWidth from '@/components/articles/ImageFullWidth'
import ImageSequence from '@/components/articles/ImageSequence'
import VideoBlock from '@/components/articles/VideoBlock'
import RichText from '@/components/articles/RichText'

import BarChart from '@/components/articles/BarChart'
import PieChart from '@/components/articles/PieChart'
import BubbleChart from '@/components/articles/BubbleChart'

import BettingBarChart from '@/components/articles/BettingBarChart'

export default {
  components: {
    Hero,
    ImageBlock,
    ImageFullWidth,
    ImageSequence,
    VideoBlock,
    RichText,

    BarChart,
    PieChart,
    BubbleChart,

    BettingBarChart
  },

  data () {
    return {
      loading: true,
      articleLoaded: false,
      articleData: {},
      suggestionData: []
    }
  },

  mounted () {
    this.loadArticle()
  },

  methods: {
    ...mapActions([
      'setupTooltips'
    ]),
    loadArticle () {
      const articleSlug = this.$router.currentRoute.path.replace('/articles/', '')
      console.log(`Target article: ${articleSlug}`)
      
      // Find the document with the associated slug
      let articleQuery = fb.articlesCollection.where('slug', '==', articleSlug).limit(1)

      articleQuery.get().then((querySnapshot) => {
        querySnapshot.forEach((doc) => {
          this.articleData = doc.data()
        })
        this.articleLoaded = true
        console.log('Got the article.')
        this.loadSuggestionData()
        this.waitForCover()
        this.$nextTick(() => {
          this.setupTooltips()
          const heroVid = document.getElementById('heroVideo')
          if (heroVid) {
            heroVid.onplay = () => {
              this.loading = false
            }
          }
        })
      }).catch((err) => {
        console.log(err)
      })
    },

    loadSuggestionData () {
      if (this.articleData.suggestions) {
        console.log('Loading suggestion data...')
        this.articleData.suggestions.forEach((suggestion) => {
          fb.indexCollection.doc(suggestion).get().then((doc) => {
            const copy = doc.data()
            copy.id = doc.id
            console.log(`Loaded ${suggestion}`)
            this.suggestionData.push(copy)
          })
        })
      } else {
        console.log('This article has no suggested stories.')
      }
    },

    waitForCover () {
      if (this.articleData.hero && this.articleData.hero.type !== 'video') {
        const hero = new Image()
        hero.onload = () => {
          console.log('Loaded cover image.')
          this.loading = false
        }
        let target = this.articleData.hero.heroDesktop
        if (this.getAspect() == 'portrait' && this.articleData.hero.heroMobile) {
          target = this.articleData.hero.heroMobile
        }
        hero.src = target
        if (hero.complete) hero.onload()
      } else if (this.articleData && this.articleData.hero.type == 'video') {
        console.log('This article has a hero video.')
      } else {
        console.log('This story has no hero images.')
        this.loading = false
      }
      setTimeout(() => {
        console.log('Cover timed out check.')
        if (this.loading) this.loading = false
      }, 4000)
    },

    getAspect () {
      if (window.innerWidth <= window.innerHeight) {
        return 'portrait'
      } else {
        return 'landscape'
      }
    }
  }
}
</script>

<style lang="scss">
@import '@/assets/style/_fonts';
@import '@/assets/style/_variables';
@import '@/assets/style/_articles';

.article-container {
  font-family: $font-body-articles;

  .article-content {
    position: relative;
    background-color: white;
  }

  .article-body {
    position: relative;
    padding-top: 48px;
    z-index: 1;
    background-color: white;

    .article-module {
      margin-bottom: 48px;

      &:first-child .container h2:first-child {
        margin-top: 0;
      }
    }
  }

  .back-container.container {
    padding-bottom: 300px;

    @include media($bp-desktop-s) {
      padding-left: $space-ml;
    }

    button {
      position: relative;
      text-transform: uppercase;
      color: rgba(0, 0, 0, 0.5);

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        border-left: solid 2px $half-black;
        border-bottom: solid 2px $half-black;
        padding-left: 1.25em;
        height: 1.25em;
        transform-origin: bottom left;
        transform: translateX(-175%) rotateZ(45deg) translateY(-50%);
        transition: .3s all;
      }

      &:hover {
        &::before {
          transform: translateX(-200%) rotateZ(45deg) translateY(-50%);
          transition: .3s all;
        }
      }
    }
  }

  h1 {
    font-family: $font-heading-articles;
    font-size: $font-size-article-xl;

    @include media($bp-tablet) {
      font-size: $font-size-article-l;
    }
  }

  h2 {
    font-family: $font-subhead-articles;
    font-size: $font-size-article-ml;
    margin: 48px 0;
    line-height: 1.333;
  }

  p {
    font-size: $font-size-article-s;
  }

  .container {
    max-width: $article-max-width;
    padding: 0 $space-xs-article;

    &.image-block,
    &.video-block {
      max-width: $image-max-width;
    }

    &.bar-chart-container {
      h1 {
        text-transform: uppercase;
        font-size: $font-size-ml;
        line-height: 1em;
      }
      h2 {
        color: $brand-grey;
        text-transform: uppercase;
        font-size: $font-size-m;
        margin: 0;
      }
    }
  }

  .keys {
    margin: 30px 0;
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    .key {
      flex: 1;
      display: flex;
      min-height: 35px;
      align-items: center;
      justify-content: center;
      position: relative;
      text-align: center;
      padding: 5px;
      &.high-number {
        flex: 0 0 calc(25% - 7.5px);
        @include media($bp-phone-l) {
          // flex: 0 0 calc(50% - 10px);
        }
      }
      &.dark {
        color: white;
      }
      span {
        line-height: 1.2em;
      }
      @include media($bp-phone-l) {
        min-height: 58px;

        span.contents {
          width: calc(100% - 4px);
        }
      }
    }
  }

  .suggestions {
    .header {
      padding: 48px;
      margin: 0;
      border-top: solid 1px rgba(0, 0, 0, 0.5);
      color: rgba(0, 0, 0, 0.5);
      text-transform: uppercase;
      text-align: center;
    }

    .suggested {
      margin-bottom: 48px;
      display: flex;
      justify-content: space-between;

      .suggestion-item {
        display: inline-block;
        width: calc(100% / 3 - #{$space-xs});

        a {
          text-decoration: none;
        }

        @include media($bp-phone-l) {
          width: calc(50% - 4px);
          &:nth-child(3) {
            display: none;
          }
        }

        .suggestion-img {
          @include bg-cover-center;
          width: 100%;
          padding-top: 100%;
          margin-bottom: $space-xs;
        }
        h3 {
          text-transform: uppercase;
          font-size: $font-size-article-s;

          @include media($bp-tablet) {
            font-size: $font-size-article-xs;
          }
        }
      }
    }
  }
}
</style>
